import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  PUBLIC_LABEL_NOTICE,
  PUBLIC_LABEL_NOTICE_VERSION,
  PUBLIC_LABEL_LOCAL_NOTICE,
  PUBLIC_LABEL_LOCAL_NOTICE_VERSION,
  publicLabelRunSchema,
  type PublicLabelRun,
} from '@sqi/shared-schemas';
import { request } from '../../lib/oem-client';
import { readLocalLabel, type LocalOcrProgress } from './local-label-ocr';
export function PublicLabelOcr({
  revisionId,
  revisionHash,
  derivativeId,
  scopeKey,
  enabled,
  onApplied,
}: {
  revisionId: string;
  revisionHash: string;
  derivativeId: string;
  scopeKey: string;
  enabled: boolean;
  onApplied: (id: string) => Promise<void>;
}) {
  const [config, setConfig] = useState<{
    enabled: boolean;
    mode: string;
    googleEnabled?: boolean;
    softwareTestEnabled?: boolean;
  } | null>(null);
  const [prepared, setPrepared] = useState<{ id: string; hash: string; png: string } | null>(null);
  const [ack, setAck] = useState(false);
  const [job, setJob] = useState<PublicLabelRun | null>(null);
  const [corrected, setCorrected] = useState('');
  const [quality, setQuality] = useState<'readable' | 'blurred' | 'incomplete'>('incomplete');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [mode, setMode] = useState<'tesseract_local' | 'gemini_free' | 'software_test'>(
    'tesseract_local',
  );
  const [progress, setProgress] = useState<LocalOcrProgress | null>(null);
  const controller = useRef<AbortController | null>(null);
  const localJob = useRef<string | null>(null);
  const cancelLocal = () => {
    controller.current?.abort();
    const id = localJob.current;
    if (id && navigator.onLine) void request(`${prefix}/${id}/cancel`, {}).catch(() => undefined);
  };
  const live = useRef(true),
    allowed = useRef(enabled);
  useLayoutEffect(() => {
    allowed.current = enabled;
  }, [enabled]);
  const prefix = `/development/${revisionId}/public-label-ocr`;
  const key = `sqi-public-label-job-v1:${scopeKey}:${revisionId}:${derivativeId}`;
  const [pendingId, setPendingId] = useState<string | null>(() => sessionStorage.getItem(key));
  const applyMutation = useRef<string | null>(null);
  const initializedResults = useRef(new Set<string>());
  function accept(value: unknown) {
    const parsed = publicLabelRunSchema.parse(value);
    if (
      parsed.revisionId !== revisionId ||
      parsed.revisionHash !== revisionHash ||
      parsed.derivativeId !== derivativeId
    )
      throw Error('stale_ocr');
    if (!live.current) return;
    setJob(parsed);
    sessionStorage.setItem(key, parsed.id);
    setPendingId(parsed.id);
    if (parsed.result && !initializedResults.current.has(parsed.id)) {
      initializedResults.current.add(parsed.id);
      setCorrected(parsed.result.output.rawText);
    }
  }
  useEffect(() => {
    live.current = true;
    void request('/public-label-ocr/config')
      .then((value) => {
        if (live.current)
          setConfig(
            value as {
              enabled: boolean;
              mode: string;
              googleEnabled?: boolean;
              softwareTestEnabled?: boolean;
            },
          );
      })
      .catch(() => {
        if (live.current) setConfig({ enabled: false, mode: 'manual' });
      });
    const id = sessionStorage.getItem(key);
    if (id && /^[a-f0-9-]{36}$/.test(id))
      void request(`${prefix}/${id}/status`, {})
        .then(accept)
        .catch(() => {
          if (live.current)
            setError('กู้สถานะไม่ได้ กรุณาตรวจสิทธิ์และการเชื่อมต่อ · ข้อความที่กรอกเองยังใช้ได้');
        });
    return () => {
      live.current = false;
      cancelLocal();
    };
    // This component is keyed to exact actor/project/revision/photo by its parent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  useLayoutEffect(() => {
    if (!enabled) controller.current?.abort();
  }, [enabled]);
  useEffect(() => {
    const offline = () => controller.current?.abort();
    window.addEventListener('offline', offline);
    return () => window.removeEventListener('offline', offline);
  }, []);
  async function scan() {
    if (!prepared) return;
    const local = mode === 'tesseract_local';
    const control = new AbortController();
    controller.current = control;
    try {
      const value = await request(prefix + '/start', {
        preparedId: prepared.id,
        preparedHash: prepared.hash,
        mode,
        noticeVersion: local ? PUBLIC_LABEL_LOCAL_NOTICE_VERSION : PUBLIC_LABEL_NOTICE_VERSION,
        acknowledged: true,
      });
      const started = publicLabelRunSchema.parse(value);
      if (local) localJob.current = started.id;
      if (!live.current || !allowed.current || control.signal.aborted) throw Error('scope_changed');
      accept(value);
      if (local) {
        if (!started.engineAssets) throw Error('unsupported_local_engine');
        const result = await readLocalLabel(
          { ...prepared, engineAssets: started.engineAssets },
          control.signal,
          (next) => {
            if (live.current && allowed.current && !control.signal.aborted) setProgress(next);
          },
        );
        if (!live.current || !allowed.current || control.signal.aborted)
          throw Error('scope_changed');
        accept(
          await request(`${prefix}/${started.id}/local-submit`, {
            preparedHash: prepared.hash,
            ...result,
          }),
        );
      } else accept(await request(`${prefix}/${started.id}/execute`, {}));
    } catch (failure) {
      if (local && localJob.current && navigator.onLine)
        void request(`${prefix}/${localJob.current}/cancel`, {}).catch(() => undefined);
      throw failure;
    } finally {
      control.abort();
      if (controller.current === control) controller.current = null;
      localJob.current = null;
      if (live.current) setProgress(null);
    }
  }
  async function run(action: () => Promise<void>) {
    if (busy || !allowed.current) return;
    setBusy(true);
    setError('');
    try {
      await action();
    } catch {
      if (live.current)
        setError(
          'ดำเนินการไม่ได้ · ภาพหรือสิทธิ์อาจเปลี่ยน โควตาเต็ม หรือบริการไม่พร้อม กรุณาถอดข้อความเองหรือเตรียมภาพใหม่',
        );
    } finally {
      if (live.current) setBusy(false);
    }
  }
  return (
    <section className="card" aria-label="อ่านฉลากสาธารณะ">
      <h3>อ่านฉลากสาธารณะด้วย OCR</h3>
      <p>
        อ่านเฉพาะภาพตัวอย่างจากฉบับที่บันทึกแล้ว OCR บนอุปกรณ์ไม่ส่งภาพไปยังผู้ให้บริการภายนอก
        ผลข้อความกลับ SQI เพื่อให้ตรวจแก้ ยังไม่ผ่านการยืนยัน และไม่ใช่ข้อมูลสูตรหรือการรับรองสินค้า
      </p>
      {mode === 'software_test' && (
        <p role="status">
          SOFTWARE TEST ONLY · ทดสอบขั้นตอนด้วยข้อมูลจำลอง ไม่ได้อ่านภาพจริงหรือเรียก Google
        </p>
      )}
      <label>
        วิธีอ่านฉลาก
        <select
          aria-label="วิธีอ่านฉลาก"
          value={mode}
          disabled={busy || !!job}
          onChange={(e) => {
            setMode(e.target.value as typeof mode);
            setAck(false);
          }}
        >
          <option value="tesseract_local">อ่านบนอุปกรณ์ · ฟรี · ไทยและอังกฤษ</option>
          {config?.googleEnabled && (
            <option value="gemini_free">Google Gemini ฟรี · ส่งภาพภายนอกเมื่อยืนยัน</option>
          )}
          {config?.softwareTestEnabled && (
            <option value="software_test">SOFTWARE TEST ONLY · ไม่ได้อ่านภาพจริง</option>
          )}
        </select>
      </label>
      {!config?.googleEnabled && (
        <p>
          Google เป็นทางเลือก ยังไม่ได้เชื่อมบัญชีฟรี ·
          ไม่มีการส่งต่ออัตโนมัติเมื่ออ่านบนอุปกรณ์ไม่ได้
        </p>
      )}
      {busy && mode === 'tesseract_local' && (
        <>
          <p role="status">
            {progress?.stage ?? 'กำลังเริ่มอ่านบนอุปกรณ์'} ·{' '}
            {Math.round((progress?.progress ?? 0) * 100)}%
          </p>
          <button className="action secondary" onClick={cancelLocal}>
            ยกเลิก OCR บนอุปกรณ์
          </button>
        </>
      )}
      {!enabled && (
        <p>บันทึกการแก้ไขก่อน และเชื่อมต่ออินเทอร์เน็ตเพื่อใช้ OCR · คำสั่งนี้ไม่เข้าคิวออฟไลน์</p>
      )}
      <button
        className="action secondary"
        disabled={!enabled || busy}
        onClick={() =>
          void run(async () => {
            const value = (await request(prefix + '/prepare', {
              hash: revisionHash,
              derivativeId,
            })) as { id: string; hash: string; png: string };
            if (!live.current || !allowed.current) return;
            setPrepared(value);
            setAck(false);
            setJob(null);
            setCorrected('');
          })
        }
      >
        เตรียมภาพตัวอย่าง OCR
      </button>
      {prepared && (
        <>
          <img
            src={'data:image/png;base64,' + prepared.png}
            alt="ภาพที่เตรียมส่งอ่านฉลากสาธารณะ"
            style={{ maxWidth: '100%', maxHeight: 260, objectFit: 'contain' }}
          />
          <label>
            <input
              type="checkbox"
              checked={ack}
              disabled={!enabled || busy}
              onChange={(e) => setAck(e.target.checked)}
            />
            {mode === 'tesseract_local' ? PUBLIC_LABEL_LOCAL_NOTICE : PUBLIC_LABEL_NOTICE}
          </label>
          <button
            className="action"
            disabled={!enabled || busy || !ack || !!job}
            onClick={() => void run(scan)}
          >
            สแกนฉลากสาธารณะ
          </button>
        </>
      )}
      {pendingId && !job && (
        <button
          className="action secondary"
          disabled={!enabled || busy}
          onClick={() =>
            void run(async () => {
              const value = await request('/public-label-ocr/config');
              if (!live.current) return;
              setConfig(
                value as {
                  enabled: boolean;
                  mode: string;
                  googleEnabled?: boolean;
                  softwareTestEnabled?: boolean;
                },
              );
              accept(await request(`${prefix}/${pendingId}/status`, {}));
            })
          }
        >
          กู้สถานะ OCR ที่บันทึกไว้
        </button>
      )}
      {job && (
        <>
          <p role="status">
            สถานะ OCR: {job.state} ·{' '}
            {job.provider === 'tesseract_local'
              ? 'อ่านบนอุปกรณ์ · ข้อความที่อุปกรณ์รายงาน ยังไม่ผ่านการยืนยัน'
              : job.provider === 'software_test'
                ? 'SOFTWARE TEST ONLY'
                : 'Google Gemini · ผู้ดูแลยืนยันบัญชีฟรี ระบบยังไม่ได้ตรวจบัญชี'}
          </p>
          <button
            className="action secondary"
            disabled={!enabled || busy}
            onClick={() =>
              void run(async () => accept(await request(`${prefix}/${job.id}/status`, {})))
            }
          >
            ตรวจสถานะ OCR
          </button>
          {['pending', 'running'].includes(job.state) && (
            <button
              className="action secondary"
              disabled={!enabled}
              onClick={() => {
                void request(`${prefix}/${job.id}/cancel`, {})
                  .then(accept)
                  .catch(() => setError('ยกเลิกไม่ได้ กรุณาตรวจการเชื่อมต่อ'));
              }}
            >
              ยกเลิก OCR
            </button>
          )}
          {job.state === 'pending' && job.provider !== 'tesseract_local' && (
            <button
              className="action secondary"
              disabled={!enabled || busy}
              onClick={() =>
                void run(async () => accept(await request(`${prefix}/${job.id}/execute`, {})))
              }
            >
              ดำเนินการ OCR ที่ยังไม่ส่ง
            </button>
          )}
          {job.state === 'pending' && job.provider === 'tesseract_local' && !busy && (
            <p>
              งานบนอุปกรณ์ไม่ได้เริ่มใหม่อัตโนมัติหลังโหลดหน้า หากหยุดไป ให้ยกเลิกแล้วเตรียมภาพใหม่
            </p>
          )}
          {['interrupted', 'failed', 'cancelled'].includes(job.state) && (
            <p>
              งานนี้จะไม่ส่งซ้ำอัตโนมัติ หากต้องการลองใหม่ ให้เตรียมภาพและยืนยันอีกครั้ง
              โควตารวมมีจำกัด
            </p>
          )}
          {job.result && (
            <>
              <label>
                ผล OCR ดิบ · แก้ไขไม่ได้
                <textarea
                  aria-label="ผล OCR ดิบ · แก้ไขไม่ได้"
                  readOnly
                  value={job.result.output.rawText}
                />
              </label>
              <p>ความไม่แน่นอน: {job.result.output.uncertainty}</p>
              {job.result.output.warnings.map((warning, i) => (
                <p key={i}>{warning}</p>
              ))}
              <label>
                ข้อความ OCR หลังตรวจภาพ
                <textarea
                  aria-label="ข้อความ OCR หลังตรวจภาพ"
                  value={corrected}
                  maxLength={4000}
                  onChange={(e) => setCorrected(e.target.value)}
                />
              </label>
              <label>
                คุณภาพ OCR หลังตรวจ
                <select
                  aria-label="คุณภาพ OCR หลังตรวจ"
                  value={quality}
                  onChange={(e) => setQuality(e.target.value as typeof quality)}
                >
                  <option value="readable">อ่านได้</option>
                  <option value="blurred">ภาพไม่ชัด</option>
                  <option value="incomplete">ข้อมูลไม่ครบ</option>
                </select>
              </label>
              <button
                className="action"
                disabled={!enabled || busy || !!job.appliedRevisionId}
                onClick={() =>
                  void run(async () => {
                    if (!allowed.current || !live.current) return;
                    const mutationKey = key + ':apply:' + job.id;
                    applyMutation.current ??=
                      sessionStorage.getItem(mutationKey) ?? crypto.randomUUID();
                    sessionStorage.setItem(mutationKey, applyMutation.current);
                    const value = (await request(`${prefix}/${job.id}/apply`, {
                      correctedOcr: corrected,
                      quality,
                      idempotencyKey: applyMutation.current,
                    })) as { id: string };
                    if (!live.current || !allowed.current) return;
                    sessionStorage.removeItem(key);
                    sessionStorage.removeItem(mutationKey);
                    await onApplied(value.id);
                  })
                }
              >
                ยืนยันข้อความและบันทึกฉบับใหม่
              </button>
            </>
          )}
        </>
      )}
      {error && <p role="alert">{error}</p>}
    </section>
  );
}
